import moment from 'moment-timezone';

/**
 * Combines date and time values for event scheduling
 * Handles all-day events, timezone conversions, and midnight-spanning events
 *
 * @param {Object} params - Parameters object
 * @param {string|Date} params.selectedDate - The date selected (from date picker)
 * @param {string|Date} params.meetingStartTime - Start time (from time picker or full datetime)
 * @param {string|Date} params.meetingEndTime - End time (from time picker or full datetime)
 * @param {boolean} params.isAllDay - Whether this is an all-day event
 * @param {string} params.timezone - User's timezone (defaults to browser timezone)
 * @returns {Object} - { formattedStartTime, formattedEndTime }
 */
export const combineDateAndTime = ({ selectedDate, meetingStartTime, meetingEndTime, isAllDay = false, timezone = null }) => {
  const tz = timezone || moment.tz.guess();

  if (isAllDay) {
    // For all-day events: just use the dates
    return {
      formattedStartTime: moment.tz(meetingStartTime, tz).format('YYYY-MM-DD'),
      formattedEndTime: moment.tz(meetingEndTime, tz).format('YYYY-MM-DD'),
    };
  }

  // For non-all-day events: combine date with time
  // Get the date from selectedDate (if provided) or from meetingStartTime
  const dateToUse = selectedDate;
  const selectedDateMoment = moment.tz(dateToUse, tz);

  // Get the time from meeting_start_time and meeting_end_time
  const startTime = moment.tz(meetingStartTime, tz);
  const endTime = moment.tz(meetingEndTime, tz);

  // Combine date with start time
  const combinedStartTime = moment.tz(
    {
      year: selectedDateMoment.year(),
      month: selectedDateMoment.month(),
      date: selectedDateMoment.date(),
      hour: startTime.hour(),
      minute: startTime.minute(),
      second: startTime.second(),
      millisecond: 0,
    },
    tz
  );

  // Combine date with end time
  let combinedEndTime = moment.tz(
    {
      year: selectedDateMoment.year(),
      month: selectedDateMoment.month(),
      date: selectedDateMoment.date(),
      hour: endTime.hour(),
      minute: endTime.minute(),
      second: endTime.second(),
      millisecond: 0,
    },
    tz
  );

  // Check if end time is before or equal to start time (indicates next day)
  if (combinedEndTime.isSameOrBefore(combinedStartTime)) {
    combinedEndTime.add(1, 'day');
  }

  return {
    formattedStartTime: combinedStartTime.toDate(),
    formattedEndTime: combinedEndTime.toDate(),
  };
};
